/**
 * RepSelector.tsx — /#/reporting/rep (no ID)
 *
 * W11-LastFourBroken: The bare /reporting/rep route previously hit the
 * 404 fallback because the only registered route required an :id param.
 * This page loads the AE roster for Tenant 1 and lets the user select
 * a rep to open their RepDetail view at /reporting/rep/:id.
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { listAEs } from "@/lib/reporting-live";
import type { UserRow } from "@/lib/reporting-live";
import { ArrowUpRight } from "lucide-react";

export default function RepSelector() {
  const [, navigate] = useLocation();
  const [aes, setAes] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listAEs();
        if (!cancelled) {
          setAes(rows);
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Failed to load reps.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "Select AE" }]}
    >
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#8E8E94",
              marginBottom: 6,
            }}
          >
            AE reporting
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0B0B0F", margin: 0 }}>
            Select an AE
          </h1>
          <p style={{ fontSize: 13, color: "#6B6B70", marginTop: 6 }}>
            Choose a rep to view their pipeline summary, account health, and recent activity.
          </p>
        </div>

        {loading && (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 10,
              padding: 24,
              textAlign: "center",
              fontSize: 13,
              color: "#8E8E94",
            }}
          >
            Loading reps...
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              padding: 16,
              color: "#991B1B",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && aes.length === 0 && (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px dashed #C9C9CC",
              borderRadius: 10,
              padding: 32,
              textAlign: "center",
              color: "#8E8E94",
              fontSize: 13,
            }}
          >
            No AEs found for this tenant. Add users with the AE role to populate this list.
          </div>
        )}

        {!loading && !error && aes.length > 0 && (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            {aes.map((ae, idx) => (
              <button
                key={ae.id}
                onClick={() => navigate(`/reporting/rep/${encodeURIComponent(ae.id)}`)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 20px",
                  background: "transparent",
                  border: "none",
                  borderTop: idx === 0 ? "none" : "1px solid #F4F4F5",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.1s",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#FAFAF7";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>
                    {ae.full_name || ae.name || ae.email}
                  </div>
                  <div style={{ fontSize: 12, color: "#8E8E94", marginTop: 2 }}>
                    {ae.email}
                    {ae.role ? ` · ${ae.role.toUpperCase()}` : ""}
                  </div>
                </div>
                <ArrowUpRight
                  style={{ width: 16, height: 16, color: "#C9C9CC", flexShrink: 0 }}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
